/**
 * Shell-level Outputs rail: list of artifacts; click opens document popup.
 * Choices / decisions stay in the conversation — this is view-only documents.
 */

import React, { useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { RunArtifact } from './artifacts';
import { ArtifactDocumentModal } from './ArtifactDocumentModal';

interface OutputsPanelProps {
  artifacts: RunArtifact[];
  className?: string;
}

export const OutputsPanel: React.FC<OutputsPanelProps> = ({
  artifacts,
  className = '',
}) => {
  const { t } = useLanguage();
  const [openId, setOpenId] = useState<string | null>(null);
  const openArtifact = artifacts.find((a) => a.id === openId) ?? null;

  return (
    <section
      className={`flex flex-col gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-3 ${className}`}
      aria-label={t('aiStudio.outputs.title')}
    >
      <h2 className="text-base font-semibold text-[var(--color-surface-text)]">
        {t('aiStudio.outputs.title')}
      </h2>
      <p className="text-sm text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.outputs.hintSessionStore')}
      </p>

      {artifacts.length === 0 ? (
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.outputs.empty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {artifacts.map((artifact) => (
            <li key={artifact.id}>
              <button
                type="button"
                onClick={() => setOpenId(artifact.id)}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-left hover:border-[var(--color-primary-bg)]"
              >
                <span className="block text-base font-medium text-[var(--color-surface-text)]">
                  {artifact.title}
                </span>
                {artifact.summary && (
                  <span className="mt-0.5 block text-sm text-[var(--color-surface-text-secondary)]">
                    {artifact.summary}
                  </span>
                )}
                {artifact.status && (
                  <span
                    className={`mt-1 inline-block rounded px-1.5 py-0.5 text-xs font-medium ${
                      artifact.status === 'saved'
                        ? 'bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]'
                        : 'border border-[var(--color-border)] text-[var(--color-surface-text-secondary)]'
                    }`}
                  >
                    {artifact.status === 'saved'
                      ? t('aiStudio.outputs.savedBadge')
                      : t('aiStudio.outputs.draftBadge')}
                  </span>
                )}
                {artifact.scope === 'store' || artifact.persistence === 'instance' ? (
                  <span className="mt-1 ml-1 inline-block rounded border border-[var(--color-border)] px-1.5 py-0.5 text-xs font-medium text-[var(--color-surface-text-secondary)]">
                    {t('aiStudio.outputs.storeBadge')}
                  </span>
                ) : artifact.status === 'saved' ? (
                  <span className="mt-1 ml-1 inline-block rounded border border-[var(--color-border)] px-1.5 py-0.5 text-xs font-medium text-[var(--color-surface-text-secondary)]">
                    {t('aiStudio.outputs.sessionBadge')}
                  </span>
                ) : null}
                {artifact.expiresAt ? (
                  <span className="mt-1 ml-1 inline-block rounded border border-[var(--color-border)] px-1.5 py-0.5 text-xs font-medium text-[var(--color-surface-text-secondary)]">
                    {t('aiStudio.outputs.ttlBadge')}
                  </span>
                ) : null}
                {artifact.stale ? (
                  <span className="mt-1 ml-1 inline-block rounded border border-[var(--color-border)] px-1.5 py-0.5 text-xs font-medium text-[var(--color-surface-text)]">
                    {t('aiStudio.outputs.staleBadge')}
                  </span>
                ) : null}
                {artifact.body._decision?.decisionStatus === 'PROVISIONAL' ||
                artifact.body._decision?.decisionMode === 'PROVISIONAL' ? (
                  <span className="mt-1 ml-1 inline-block rounded border border-[var(--color-border)] px-1.5 py-0.5 text-xs font-medium text-[var(--color-surface-text-secondary)]">
                    {t('aiStudio.outputs.provisionalBadge')}
                  </span>
                ) : null}
                {Array.isArray(artifact.body._decision?.evidenceRefs) &&
                artifact.body._decision.evidenceRefs.length > 0 ? (
                  <span className="mt-1 ml-1 inline-block rounded border border-[var(--color-border)] px-1.5 py-0.5 text-xs font-medium text-[var(--color-surface-text-secondary)]">
                    {t('aiStudio.outputs.evidenceBadge').replace(
                      '{count}',
                      String(artifact.body._decision.evidenceRefs.length)
                    )}
                  </span>
                ) : null}
                <span className="mt-1 block text-sm text-[var(--color-info)]">
                  {t('aiStudio.outputs.openDocument')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <ArtifactDocumentModal artifact={openArtifact} onClose={() => setOpenId(null)} />
    </section>
  );
};
